import { useState, useEffect, useMemo } from "react"
import { useStores } from "stores/root-store"
import { rootStore } from "stores/root-store"
import { storageName } from "shared/constants/storage-constant"
import UploadDialog from "components/common/upload/upload-dialog"
import template from "assets/templates/Despatch-Manifest-v2.5-Multi-Warehouse-IP.zip"
import { columnsUpload, filterData, columns, hiddenColumns, columnsUploadFunctionsMap } from "./constants"
import SaleOrderForm from "components/sale-order/dialog/sale-order-form"
import { observer } from "mobx-react-lite"
import CommonPage from "components/common/common-page"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getModuleName } from "utils/app-helper"
import * as StorageHelper from "utils/storage-helper"
import { parsePresetFilters } from "utils/preset-filters"

// Multi-select filters a link into this page (e.g. a home dashboard tile) can
// pre-set via the query string; each accepts a comma-separated list of values.
const PRESET_MULTI_FILTERS = ["order_status", "exception_type", "warehouse", "carrier", "country", "sales_channel"]

const SaleOrders = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [openUpload, setOpenUpload] = useState(false)

  const { SaleOrderStore } = useStores()

  const moduleName = getModuleName(storageName.salesOrderFilter)

  const presetFilters = useMemo(() => parsePresetFilters(searchParams, PRESET_MULTI_FILTERS), [searchParams])

  useEffect(() => {
    rootStore.setOpenForm(false)
    rootStore.setScreenTitle("Sales Order Search")
    SaleOrderStore.resetData()

    const importingModule = StorageHelper.getStorage(storageName.importingModule)
    if (importingModule === moduleName) setOpenUpload(true)

    return () => rootStore.setScreenTitle("")
  }, [])

  const refreshDataIfNeeded = () => {
    if (SaleOrderStore.searchText) SaleOrderStore.loadData()
  }

  const handleClickOpen = () => {
    SaleOrderStore.setDeliveryInfo(null)
    rootStore.setScreenTitle("")
    rootStore.setOpenForm(true)
  }

  const handleClickOpenUpload = () => {
    setOpenUpload(true)
  }

  const handleUploadClose = () => {
    setOpenUpload(false)
    refreshDataIfNeeded()
  }

  const handleClose = () => {
    SaleOrderStore.setSelectedId("")
    rootStore.setScreenTitle("Sales Order Search")
    rootStore.setOpenForm(false)
  }

  const handleSave = () => {
    handleClose()
    refreshDataIfNeeded()
  }

  const handleRowClicked = params => {
    navigate(`/sales-order/${encodeURIComponent(params.row["ERP_ORDER"])}/${encodeURIComponent(params.row["ID"])}`)
  }

  const handleBulkDelete = selectedIDs => {
    return SaleOrderStore.batchDelete(selectedIDs)
  }

  if (rootStore.openForm) return <SaleOrderForm onSave={handleSave} onClose={handleClose} />

  return (
    <>
      <CommonPage
        DataStore={SaleOrderStore}
        filterData={filterData}
        presetFilters={presetFilters}
        searchPlaceholder={"Search by Order Number, SKU Code, Customer PO, Tracking Number, Contact Name, Attention To, or Company."}
        filterStorageName={storageName.salesOrderFilter}
        allowArchivalSearch={true}
        columns={columns}
        hiddenColumns={hiddenColumns}
        gridStorageName={storageName.salesOrderColumn}
        searchRequiredWarningMsg="Search for a specific sales order and select a filter to narrow down your search"
        noDataMsg="The Search term could not be located in the system. This could be because the order is older than 30 days, and has already been archived. Please contact ops@estorelogistics.com.au if
                  you believe this error is incorrect."
        exportFileName="Express_SalesOrder"
        handleAddClicked={handleClickOpen}
        handleUploadClicked={handleClickOpenUpload}
        handleRowClicked={handleRowClicked}
        handleBulkDelete={handleBulkDelete}
      />

      <UploadDialog
        title={"Sales order"}
        moduleName={moduleName}
        columns={columnsUpload}
        columnsUploadFunctionsMap={columnsUploadFunctionsMap}
        idColumn={"erp_order"}
        downloadLink={template}
        open={openUpload}
        onClose={handleUploadClose}
        DataStore={SaleOrderStore}
      />
    </>
  )
}

export default observer(SaleOrders)
